import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Bookmark, CalendarDays, Captions, CheckCircle2, Clock3, Database, ExternalLink, Eye, EyeOff, FolderCheck, Hash, Highlighter, Image as ImageIcon, LibraryBig, ListVideo, LoaderCircle, RadioTower, RotateCcw, Search, ShieldCheck, Sparkles, Tags, X } from 'lucide-react'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FilmDetailsDialog } from '../../components/FilmDetailsDialog'
import { SenderLogo } from '../../components/SenderLogo'
import { DownloadDialog } from '../catalog/DownloadDialog'
import { SubscriptionDialog } from '../subscriptions/SubscriptionsView'
import { api, formatDuration, queryString, type ContentTag, type ContentType, type Film, type MediaRoot, type Page, type SaveSubscription, type SeriesEpisode, type SeriesIndexStatus, type SeriesNumberingScheme, type SeriesSummary } from '../../lib/api'
import { loadLocalPreferences, saveLocalPreferences } from '../../lib/localPreferences'

interface Props { mediaRoots: MediaRoot[] }
type Scope = 'DISCOVER' | 'MINE'
type SeriesSort = 'DISCOVERY' | 'TITLE' | 'LATEST' | 'EPISODES'

interface SeriesMetadata {
  provider: string
  matchStatus: 'MATCHED' | 'NOT_FOUND' | 'AMBIGUOUS' | 'ERROR'
  providerId: string
  title: string
  originalTitle: string
  overview: string
  genres: string[]
  countries: string[]
  language: string | null
  network: string | null
  status: string | null
  premieredOn: string | null
  endedOn: string | null
  averageRuntimeMinutes: number | null
  seasonCount: number | null
  episodeCount: number | null
  providerUrl: string | null
  officialSite: string | null
  attribution: string | null
  artworkSourceUrl: string | null
  artworkLicense: string | null
  artworkLicenseUrl: string | null
  artworkAvailable: boolean
  matchConfidence: number
  errorDetail: string | null
  retryAfter: string | null
  updatedAt: string
}

type EnrichedSeriesSummary = SeriesSummary & { categories: string[]; metadata: SeriesMetadata | null }
interface SeriesFacet { value: string; count: number }
interface SeriesFacets { senders: SeriesFacet[]; categories: SeriesFacet[] }

const SERIES_PREFERENCES_KEY = 'meine-mediathek.series-filter-preferences.v1'

interface SeriesFilterPreferences {
  query: string
  scope: Scope
  sender: string
  category: string
  newOnly: boolean
  followedOnly: boolean
  subscribedOnly: boolean
  numberedOnly: boolean
  artworkOnly: boolean
  sort: SeriesSort
}

function loadSeriesPreferences(): SeriesFilterPreferences {
  const defaults: SeriesFilterPreferences = {
    query: '', scope: 'DISCOVER', sender: '', category: '', newOnly: false, followedOnly: false,
    subscribedOnly: false, numberedOnly: false, artworkOnly: false, sort: 'DISCOVERY',
  }
  const stored = loadLocalPreferences(SERIES_PREFERENCES_KEY, defaults)
  const scopes: Scope[] = ['DISCOVER', 'MINE']
  const sorts: SeriesSort[] = ['DISCOVERY', 'TITLE', 'LATEST', 'EPISODES']
  return {
    query: typeof stored.query === 'string' ? stored.query : '',
    scope: scopes.includes(stored.scope) ? stored.scope : 'DISCOVER',
    sender: typeof stored.sender === 'string' ? stored.sender : '',
    category: typeof stored.category === 'string' ? stored.category : '',
    newOnly: typeof stored.newOnly === 'boolean' ? stored.newOnly : false,
    followedOnly: typeof stored.followedOnly === 'boolean' ? stored.followedOnly : false,
    subscribedOnly: typeof stored.subscribedOnly === 'boolean' ? stored.subscribedOnly : false,
    numberedOnly: typeof stored.numberedOnly === 'boolean' ? stored.numberedOnly : false,
    artworkOnly: typeof stored.artworkOnly === 'boolean' ? stored.artworkOnly : false,
    sort: sorts.includes(stored.sort) ? stored.sort : 'DISCOVERY',
  }
}

export function SeriesView({ mediaRoots }: Props) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()
  const { seriesId } = useParams()
  const [initialPreferences] = useState(loadSeriesPreferences)
  const [scope, setScope] = useState<Scope>(initialPreferences.scope)
  const [searchInput, setSearchInput] = useState(initialPreferences.query)
  const [query, setQuery] = useState(initialPreferences.query.trim())
  const [sender, setSender] = useState(initialPreferences.sender)
  const [category, setCategory] = useState(initialPreferences.category)
  const [newOnly, setNewOnly] = useState(initialPreferences.newOnly)
  const [followedOnly, setFollowedOnly] = useState(initialPreferences.followedOnly)
  const [subscribedOnly, setSubscribedOnly] = useState(initialPreferences.subscribedOnly)
  const [numberedOnly, setNumberedOnly] = useState(initialPreferences.numberedOnly)
  const [artworkOnly, setArtworkOnly] = useState(initialPreferences.artworkOnly)
  const [sort, setSort] = useState<SeriesSort>(initialPreferences.sort)
  const [subscriptionSeries, setSubscriptionSeries] = useState<EnrichedSeriesSummary | null>(null)
  const [episodeFilmId, setEpisodeFilmId] = useState<string | null>(null)
  const [downloadFilm, setDownloadFilm] = useState<Film | null>(null)
  const [episodeSubscription, setEpisodeSubscription] = useState<Partial<SaveSubscription> | null>(null)
  const loadMoreRef = useRef<HTMLDivElement>(null)
  const completedIndexRef = useRef<string | null>(null)

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(searchInput.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [searchInput])

  useEffect(() => {
    saveLocalPreferences(SERIES_PREFERENCES_KEY, {
      query: searchInput, scope, sender, category, newOnly, followedOnly,
      subscribedOnly, numberedOnly, artworkOnly, sort,
    } satisfies SeriesFilterPreferences)
  }, [searchInput, scope, sender, category, newOnly, followedOnly, subscribedOnly, numberedOnly, artworkOnly, sort])

  const status = useQuery({
    queryKey: ['series', 'status'],
    queryFn: () => api<SeriesIndexStatus>('/api/v1/series/status'),
    refetchInterval: (current) => current.state.data?.complete ? false : 2_000,
  })
  const facets = useQuery({
    queryKey: ['series', 'facets'],
    queryFn: () => api<SeriesFacets>('/api/v1/series/facets'),
    refetchInterval: 60_000,
  })
  const series = useInfiniteQuery({
    queryKey: ['series', scope, query, sender, category, newOnly, followedOnly,
      subscribedOnly, numberedOnly, artworkOnly, sort],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => api<Page<EnrichedSeriesSummary>>(`/api/v1/series?${queryString({
      scope, query, sender, category, newOnly: newOnly || undefined,
      followedOnly: followedOnly || undefined, subscribedOnly: subscribedOnly || undefined,
      numberedOnly: numberedOnly || undefined, artworkOnly: artworkOnly || undefined,
      sort, page: pageParam, size: 48,
    })}`),
    getNextPageParam: (lastPage) => lastPage.page + 1 < lastPage.totalPages ? lastPage.page + 1 : undefined,
    refetchInterval: 60_000,
  })

  useEffect(() => {
    const completedGeneration = status.data?.complete ? status.data.indexedGeneration : null
    if (!completedGeneration || completedIndexRef.current === completedGeneration) return
    completedIndexRef.current = completedGeneration
    series.refetch()
    facets.refetch()
  }, [facets.refetch, series.refetch, status.data?.complete, status.data?.indexedGeneration])

  const items = series.data?.pages.flatMap((page) => page.items) ?? []
  const listed = useMemo(() => items.find((item) => item.id === seriesId), [items, seriesId])
  const detail = useQuery({
    queryKey: ['series', 'detail', seriesId],
    queryFn: () => api<EnrichedSeriesSummary>(`/api/v1/series/${encodeURIComponent(seriesId!)}`),
    enabled: Boolean(seriesId && !listed),
    initialData: listed,
    refetchInterval: seriesId ? 60_000 : false,
  })
  const selected = seriesId ? (listed ?? detail.data ?? null) : null
  const episodeFilm = useQuery({
    queryKey: ['catalog', 'detail', episodeFilmId],
    queryFn: () => api<Film>(`/api/v1/catalog/${encodeURIComponent(episodeFilmId!)}`),
    enabled: Boolean(episodeFilmId),
  })
  const follow = useMutation({
    mutationFn: (item: EnrichedSeriesSummary) => api<EnrichedSeriesSummary>(
      `/api/v1/series/${encodeURIComponent(item.id)}/follow`,
      { method: item.followed ? 'DELETE' : 'PUT' },
    ),
    onSuccess: (updated) => {
      queryClient.setQueryData(['series', 'detail', updated.id], updated)
      queryClient.invalidateQueries({ queryKey: ['series'] })
    },
  })
  const episodeState = useMutation({
    mutationFn: ({ film, bookmarked, seen }: { film: Film; bookmarked: boolean; seen: boolean }) => api(
      `/api/v1/library-state/${film.id}`,
      { method: 'PUT', body: JSON.stringify({ bookmarked, seen, note: '' }) },
    ),
    onSuccess: (_data, values) => queryClient.setQueryData<Film>(
      ['catalog', 'detail', values.film.id],
      (current) => current ? { ...current, bookmarked: values.bookmarked, seen: values.seen } : current,
    ),
  })
  const episodeClassification = useMutation({
    mutationFn: ({ film, contentType, tags }: { film: Film; contentType: ContentType; tags: ContentTag[] }) => api<Film>(
      `/api/v1/catalog/${encodeURIComponent(film.id)}/classification`,
      { method: 'PUT', body: JSON.stringify({ contentType, tags }) },
    ),
    onSuccess: (updated) => queryClient.setQueryData(['catalog', 'detail', updated.id], updated),
  })

  useEffect(() => {
    const target = loadMoreRef.current
    if (!target || !series.hasNextPage) return
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting) && !series.isFetchingNextPage) series.fetchNextPage()
    }, { rootMargin: '500px 0px' })
    observer.observe(target)
    return () => observer.disconnect()
  }, [series.fetchNextPage, series.hasNextPage, series.isFetchingNextPage])

  const openDetails = (item: EnrichedSeriesSummary) => navigate(`/serien/${item.id}`, { state: { seriesOverlay: true } })
  const closeDetails = () => {
    setEpisodeFilmId(null)
    const state = location.state as { seriesOverlay?: boolean } | null
    if (state?.seriesOverlay) navigate(-1)
    else navigate('/serien', { replace: true })
  }
  const openSubscription = (item: EnrichedSeriesSummary) => {
    if (item.subscription) navigate('/abos')
    else setSubscriptionSeries(item)
  }
  const resetFilters = () => {
    setSearchInput(''); setSender(''); setCategory(''); setNewOnly(false); setFollowedOnly(false)
    setSubscribedOnly(false); setNumberedOnly(false); setArtworkOnly(false); setSort('DISCOVERY')
  }
  const activeFilterCount = [Boolean(query), Boolean(sender), Boolean(category), newOnly,
    followedOnly, subscribedOnly, numberedOnly, artworkOnly, sort !== 'DISCOVERY'].filter(Boolean).length
  const total = series.data?.pages[0]?.totalItems ?? 0
  const emptyMine = scope === 'MINE'

  return <section className="page series-page">
    <div className="series-intro panel">
      <div className="series-intro-copy">
        <span className="eyebrow"><Sparkles />{t('series.eyebrow')}</span>
        <h1>{t('series.title')}</h1>
        <p>{t('series.subtitle')}</p>
      </div>
      <div className="series-scope" role="tablist">
        <button className={scope === 'DISCOVER' ? 'active' : ''} onClick={() => setScope('DISCOVER')} role="tab" aria-selected={scope === 'DISCOVER'}><Sparkles />{t('series.discover')}</button>
        <button className={scope === 'MINE' ? 'active' : ''} onClick={() => setScope('MINE')} role="tab" aria-selected={scope === 'MINE'}><Bookmark />{t('series.mine')}</button>
      </div>
    </div>

    <div className="series-filter-panel panel">
      <div className="search-box series-search"><Search /><input value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder={t('series.searchPlaceholder')} aria-label={t('series.searchPlaceholder')} />{searchInput && <button onClick={() => setSearchInput('')} aria-label={t('common.reset')}><X /></button>}</div>
      <label className="compact-select"><span>{t('series.senderFilter')}</span><select value={sender} onChange={(event) => setSender(event.target.value)}><option value="">{t('common.all')}</option>{facets.data?.senders.map((item) => <option key={item.value} value={item.value}>{item.value} · {item.count.toLocaleString('de-DE')}</option>)}</select></label>
      <label className="compact-select"><span>{t('series.categoryFilter')}</span><select value={category} onChange={(event) => setCategory(event.target.value)}><option value="">{t('common.all')}</option>{facets.data?.categories.map((item) => <option key={item.value} value={item.value}>{categoryLabel(item.value, t)} · {item.count.toLocaleString('de-DE')}</option>)}</select></label>
      <label className="compact-select"><span>{t('series.sort')}</span><select value={sort} onChange={(event) => setSort(event.target.value as SeriesSort)}><option value="DISCOVERY">{t('series.sortDiscovery')}</option><option value="LATEST">{t('series.sortLatest')}</option><option value="TITLE">{t('series.sortTitle')}</option><option value="EPISODES">{t('series.sortEpisodes')}</option></select></label>
      <div className="series-filter-chips">
        <SeriesFilterChip active={newOnly} onClick={() => setNewOnly(!newOnly)} icon={<Sparkles />} label={t('series.filterNew')} />
        <SeriesFilterChip active={followedOnly} onClick={() => setFollowedOnly(!followedOnly)} icon={<Eye />} label={t('series.filterFollowed')} />
        <SeriesFilterChip active={subscribedOnly} onClick={() => setSubscribedOnly(!subscribedOnly)} icon={<RadioTower />} label={t('series.filterSubscribed')} />
        <SeriesFilterChip active={numberedOnly} onClick={() => setNumberedOnly(!numberedOnly)} icon={<Hash />} label={t('series.filterNumbered')} />
        <SeriesFilterChip active={artworkOnly} onClick={() => setArtworkOnly(!artworkOnly)} icon={<ImageIcon />} label={t('series.filterArtwork')} />
        {activeFilterCount > 0 && <button className="series-filter-reset" onClick={resetFilters}><RotateCcw />{t('series.resetFilters')}<b>{activeFilterCount}</b></button>}
      </div>
    </div>

    {status.data && !status.data.complete && <div className="series-indexing panel"><LoaderCircle className="spin" /><span>{t('series.indexing', { count: status.data.processedCount.toLocaleString('de-DE') })}</span></div>}
    {series.isLoading && <div className="panel discovery-state">{t('common.loading')}</div>}
    {series.isError && <div className="panel discovery-state error">{series.error.message}</div>}
    {series.data && items.length === 0 && <div className="panel discovery-state"><LibraryBig /><strong>{t(emptyMine ? 'series.mineEmptyTitle' : 'series.emptyTitle')}</strong><p>{t(emptyMine ? 'series.mineEmptyHint' : 'series.emptyHint')}</p>{activeFilterCount > 0 && <button className="quiet-button" onClick={resetFilters}><RotateCcw />{t('series.resetFilters')}</button>}{emptyMine && activeFilterCount === 0 && <button className="quiet-button" onClick={() => setScope('DISCOVER')}><Sparkles />{t('series.discover')}</button>}</div>}
    {items.length > 0 && <>
      <div className="discovery-summary"><strong>{t('series.resultCount', { count: total })}</strong><span>{t('series.precisionHint')}</span></div>
      <div className="series-grid">
        {items.map((item) => <SeriesCard key={item.id} item={item} followPending={follow.isPending}
          onOpen={openDetails} onFollow={() => follow.mutate(item)} onSubscription={() => openSubscription(item)} />)}
      </div>
      <div className="discovery-load-more" ref={loadMoreRef}>{series.isFetchingNextPage ? t('common.loading') : series.hasNextPage ? t('newMovies.moreOnScroll') : t('newMovies.end')}</div>
    </>}

    {selected && <SeriesDetails item={selected} onClose={closeDetails}
      onFollow={() => follow.mutate(selected)} followPending={follow.isPending}
      onSubscription={() => openSubscription(selected)} onEpisode={setEpisodeFilmId} />}
    {episodeFilm.data && <FilmDetailsDialog film={episodeFilm.data} libraryStatus={episodeFilm.data.library}
      stateUpdating={episodeState.isPending} stateError={episodeState.isError}
      classificationUpdating={episodeClassification.isPending} classificationError={episodeClassification.isError}
      onClassificationChange={(contentType, tags) => episodeClassification.mutate({ film: episodeFilm.data, contentType, tags })}
      onClose={() => setEpisodeFilmId(null)} onDownload={() => { setDownloadFilm(episodeFilm.data); setEpisodeFilmId(null) }}
      onSubscribe={() => setEpisodeSubscription(episodeSubscriptionInitial(episodeFilm.data))}
      onBookmark={() => episodeState.mutate({ film: episodeFilm.data, bookmarked: !episodeFilm.data.bookmarked, seen: episodeFilm.data.seen })}
      onSeen={() => episodeState.mutate({ film: episodeFilm.data, bookmarked: episodeFilm.data.bookmarked, seen: !episodeFilm.data.seen })} />}
    {episodeFilmId && episodeFilm.isLoading && <div className="modal-backdrop nested-modal-loading"><LoaderCircle className="spin" /><span>{t('series.loadingContent')}</span></div>}
    {downloadFilm && <DownloadDialog film={downloadFilm} mediaRoots={mediaRoots} onClose={() => setDownloadFilm(null)} />}
    {subscriptionSeries && <SubscriptionDialog subscription={null} mediaRoots={mediaRoots}
      initial={seriesSubscriptionInitial(subscriptionSeries)}
      onClose={() => { setSubscriptionSeries(null); queryClient.invalidateQueries({ queryKey: ['series'] }) }} />}
    {episodeSubscription && <SubscriptionDialog subscription={null} mediaRoots={mediaRoots}
      initial={episodeSubscription} onClose={() => setEpisodeSubscription(null)} />}
  </section>
}

function SeriesFilterChip({ active, onClick, icon, label }: {
  active: boolean; onClick: () => void; icon: ReactNode; label: string
}) {
  return <button type="button" className={`series-filter-chip ${active ? 'active' : ''}`} aria-pressed={active} onClick={onClick}>{icon}<span>{label}</span></button>
}

function SeriesCard({ item, followPending, onOpen, onFollow, onSubscription }: {
  item: EnrichedSeriesSummary
  followPending: boolean
  onOpen: (item: EnrichedSeriesSummary) => void
  onFollow: () => void
  onSubscription: () => void
}) {
  const { t } = useTranslation()
  const metadata = item.metadata?.matchStatus === 'MATCHED' ? item.metadata : null
  return <article className={`series-card ${metadata?.artworkAvailable ? 'with-artwork' : ''}`} tabIndex={0} role="button"
    aria-label={t('series.openDetails', { title: item.title })}
    onClick={() => onOpen(item)}
    onKeyDown={(event) => { if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); onOpen(item) } }}>
    {metadata?.artworkAvailable
      ? <div className="series-card-artwork"><img src={seriesArtworkUrl(item)} alt="" loading="lazy" /></div>
      : <div className="series-card-visual"><LibraryBig /><span>{item.title.slice(0, 1).toLocaleUpperCase('de-DE')}</span></div>}
    <div className="series-card-heading">
      <SenderLogo sender={item.sender} compact />
      <div><span className={`series-discovery-kind ${item.discoveryKind.toLowerCase()}`}>{discoveryLabel(item.discoveryKind, t)}</span><h2>{item.title}</h2></div>
    </div>
    <div className="series-status-row">
      {item.followed && <span className="followed"><Eye />{t('series.followed')}</span>}
      {item.subscription && <span className={item.subscription.active ? 'subscription-active' : 'subscription-paused'}><RadioTower />{t(item.subscription.active ? 'series.activeSubscription' : 'series.pausedSubscription')}</span>}
      {metadata && <span className="metadata"><Database />TVmaze</span>}
      {item.metadata && item.metadata.matchStatus !== 'MATCHED' && <span className="metadata-problem"><Database />{t(`series.metadataStates.${item.metadata.matchStatus}`)}</span>}
    </div>
    {item.categories.length > 0 && <div className="series-category-row">{item.categories.slice(0, 3).map((value) => <span key={value}>{categoryLabel(value, t)}</span>)}</div>}
    <p>{item.description || item.latestEpisode?.title || '\u2014'}</p>
    {item.latestEpisode && <div className="series-latest"><small>{t('series.latestEpisode')}</small><strong>{numberingLabel(item.latestEpisode, t)}</strong><span>{item.latestEpisode.title}</span></div>}
    <div className="series-counts">
      <span><ListVideo />{t('series.availableEpisodes', { count: item.availableEpisodeCount })}</span>
      {metadata?.seasonCount != null && <span><Tags />{t('series.providerSeasonsShort', { count: metadata.seasonCount })}</span>}
      {item.downloadedEpisodeCount > 0 && <span className="downloaded"><FolderCheck />{t('series.downloadedEpisodes', { count: item.downloadedEpisodeCount })}</span>}
      {item.queuedEpisodeCount > 0 && <span><Clock3 />{t('series.queuedEpisodes', { count: item.queuedEpisodeCount })}</span>}
    </div>
    <footer>
      <button className={`quiet-button series-follow-button ${item.followed ? 'active' : ''}`} disabled={followPending} onClick={(event) => { event.stopPropagation(); onFollow() }}>{item.followed ? <EyeOff /> : <Eye />}{t(item.followed ? 'series.unfollow' : 'series.follow')}</button>
      <button className={`primary-button ${item.subscription ? 'subscription-present' : ''}`} onClick={(event) => { event.stopPropagation(); onSubscription() }}><RadioTower />{t(item.subscription ? 'series.manageSubscription' : 'series.createSubscription')}</button>
    </footer>
  </article>
}

function SeriesDetails({ item, onClose, onFollow, followPending, onSubscription, onEpisode }: {
  item: EnrichedSeriesSummary
  onClose: () => void
  onFollow: () => void
  followPending: boolean
  onSubscription: () => void
  onEpisode: (filmId: string) => void
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const episodes = useInfiniteQuery({
    queryKey: ['series', 'episodes', item.id],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => api<Page<SeriesEpisode>>(`/api/v1/series/${encodeURIComponent(item.id)}/episodes?${queryString({ page: pageParam, size: 50 })}`),
    getNextPageParam: (lastPage) => lastPage.page + 1 < lastPage.totalPages ? lastPage.page + 1 : undefined,
  })
  const items = episodes.data?.pages.flatMap((page) => page.items) ?? []
  const metadataState = item.metadata
  const metadata = metadataState?.matchStatus === 'MATCHED' ? metadataState : null
  const retryMetadata = useMutation({
    mutationFn: () => api<EnrichedSeriesSummary>(`/api/v1/series/${encodeURIComponent(item.id)}/metadata/retry`, { method: 'POST' }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['series', 'detail', updated.id], updated)
      queryClient.invalidateQueries({ queryKey: ['series'] })
    },
  })
  return <div className="modal-backdrop" onMouseDown={onClose}><section className="modal-card series-detail" onMouseDown={(event) => event.stopPropagation()}>
    <header className="modal-header"><div><span className="eyebrow"><LibraryBig />{discoveryLabel(item.discoveryKind, t)}</span><h2>{item.title}</h2><SenderLogo sender={item.sender} compact /></div><button className="icon-button" onClick={onClose} aria-label={t('common.close')}><X /></button></header>
    <div className={`series-detail-hero ${metadata?.artworkAvailable ? 'with-artwork' : ''}`}>
      {metadata?.artworkAvailable && <div className="series-detail-poster"><img src={seriesArtworkUrl(item)} alt="" /></div>}
      <div className="series-detail-summary">
        {item.categories.length > 0 && <div className="series-detail-categories">{item.categories.map((value) => <span key={value}>{categoryLabel(value, t)}</span>)}</div>}
        <p>{item.description || item.latestEpisode?.title || '\u2014'}</p>
        <div className="series-detail-facts">
          <span><ListVideo /><strong>{item.availableEpisodeCount}</strong>{t('series.availableNow')}</span>
          {metadata?.seasonCount != null && <span><Tags /><strong>{metadata.seasonCount}</strong>{t('series.providerSeasons')}</span>}
          {metadata?.episodeCount != null && <span><Database /><strong>{metadata.episodeCount}</strong>{t('series.providerEpisodes')}</span>}
          {item.downloadedEpisodeCount > 0 && <span><CheckCircle2 /><strong>{item.downloadedEpisodeCount}</strong>{t('series.downloadedEpisodes', { count: item.downloadedEpisodeCount })}</span>}
          {item.unnumberedEpisodeCount > 0 && <span><Highlighter /><strong>{item.unnumberedEpisodeCount}</strong>{t('series.unnumberedEpisodes', { count: item.unnumberedEpisodeCount })}</span>}
        </div>
        {metadata && <div className="series-metadata-line">
          {metadata.premieredOn && <span>{new Date(metadata.premieredOn).getFullYear()}</span>}
          {metadata.status && <span>{metadataStatusLabel(metadata.status, t)}</span>}
          {metadata.network && <span>{metadata.network}</span>}
          {metadata.language && <span>{languageLabel(metadata.language, t)}</span>}
          {metadata.averageRuntimeMinutes && <span>{metadata.averageRuntimeMinutes} min</span>}
        </div>}
        <small className="series-grouping-evidence"><ShieldCheck />{t('series.groupingEvidence', { confidence: item.groupingConfidence })}</small>
        {metadata?.providerUrl && <a className="series-metadata-source" href={metadata.providerUrl} target="_blank" rel="noopener noreferrer"><ExternalLink />{metadata.attribution || t('series.tvmazeAttribution')}</a>}
        {!metadataState && <small className="series-metadata-pending"><Database />{t('series.metadataPending')}</small>}
        {metadataState && metadataState.matchStatus !== 'MATCHED' && <div className={`series-metadata-state ${metadataState.matchStatus.toLowerCase()}`}>
          <Database />
          <div><strong>{t(`series.metadataStates.${metadataState.matchStatus}`)}</strong><span>{t(`series.metadataStateHints.${metadataState.matchStatus}`)}</span></div>
          <button type="button" className="quiet-button" disabled={retryMetadata.isPending} onClick={() => retryMetadata.mutate()}><RotateCcw />{t('series.retryMetadata')}</button>
        </div>}
      </div>
    </div>
    <div className="series-detail-actions">
      <button className={`quiet-button ${item.followed ? 'active' : ''}`} disabled={followPending} onClick={onFollow}>{item.followed ? <EyeOff /> : <Eye />}{t(item.followed ? 'series.unfollow' : 'series.follow')}</button>
      <button className={`primary-button ${item.subscription ? 'subscription-present' : ''}`} onClick={onSubscription}><RadioTower />{t(item.subscription ? 'series.manageSubscription' : 'series.createSubscription')}</button>
    </div>
    <section className="series-episode-section"><header><ListVideo /><strong>{t('series.episodes')}</strong><span>{episodes.data?.pages[0]?.totalItems ?? 0}</span></header>
      {episodes.isLoading && <div className="table-loading">{t('common.loading')}</div>}
      {episodes.isError && <div className="inline-error">{episodes.error.message}</div>}
      <div className="series-episode-list">{items.map((episode) => <button type="button" onClick={() => onEpisode(episode.filmId)} key={episode.filmId} title={t('series.openCatalogEpisode')}>
        <div className="series-episode-number">{shortNumberingLabel(episode)}</div>
        <div><strong>{episode.title}</strong><span>{numberingLabel(episode, t)}</span><small>{episode.broadcastAt ? new Date(episode.broadcastAt).toLocaleDateString('de-DE') : '\u2014'} {'\u00b7'} {formatDuration(episode.durationSeconds)}</small></div>
        <div className="series-episode-features">{episode.highQuality && <span>HD</span>}{episode.subtitles && <span><Captions />UT</span>}<CalendarDays /></div>
      </button>)}</div>
      {episodes.hasNextPage && <button className="quiet-button series-episodes-more" disabled={episodes.isFetchingNextPage} onClick={() => episodes.fetchNextPage()}>{episodes.isFetchingNextPage ? t('common.loading') : t('subscriptions.loadMore')}</button>}
    </section>
  </section></div>
}

function seriesSubscriptionInitial(item: EnrichedSeriesSummary): Partial<SaveSubscription> {
  return { name: item.title, programmeId: item.id, sender: '', topic: '', titleFilter: '',
    topicTitleFilter: '', anywhereFilter: '', onlyNew: false }
}

function episodeSubscriptionInitial(film: Film): Partial<SaveSubscription> {
  return {
    name: film.topic || film.title, active: true, sender: film.sender, topic: film.topic,
    titleFilter: '', topicTitleFilter: '', anywhereFilter: '', filterMode: 'TEXT',
    minimumDurationSeconds: null, maximumDurationSeconds: null, onlyNew: false,
    excludeAudioDescription: true, excludeSignLanguage: true, excludeOriginalLanguage: false,
    executionMode: 'AUTO', quality: 'HIGH', destinationSubpath: '', subtitlesEnabled: true,
    infoFileEnabled: true,
  }
}

function seriesArtworkUrl(item: EnrichedSeriesSummary) {
  const revision = item.metadata?.updatedAt
  return `/api/v1/series/${encodeURIComponent(item.id)}/artwork${revision ? `?v=${encodeURIComponent(revision)}` : ''}`
}

function categoryLabel(value: string, t: (key: string) => string) {
  if (['DOCUMENTARY', 'CHILDREN', 'KNOWLEDGE', 'MUSIC'].includes(value)) {
    return t(`sendungen.classification.types.${value}`)
  }
  const key = value.toLocaleLowerCase('en-US').replaceAll('-', '_').replaceAll(' ', '_')
  const translated = t(`series.categories.${key}`)
  return translated === `series.categories.${key}` ? value : translated
}

function metadataStatusLabel(value: string, t: (key: string) => string) {
  const key = value.toLocaleLowerCase('en-US').replaceAll(' ', '_')
  const translated = t(`series.providerStatuses.${key}`)
  return translated === `series.providerStatuses.${key}` ? value : translated
}

function languageLabel(value: string, t: (key: string) => string) {
  const key = value.toLocaleLowerCase('en-US').replaceAll(' ', '_')
  const translated = t(`series.languages.${key}`)
  return translated === `series.languages.${key}` ? value : translated
}

function discoveryLabel(kind: SeriesSummary['discoveryKind'], t: (key: string) => string) {
  if (kind === 'NEW_SERIES') return t('series.newSeries')
  if (kind === 'NEWLY_CLASSIFIED') return t('series.newlyClassified')
  return t('series.baseline')
}

type Numbered = {
  numberingScheme: SeriesNumberingScheme
  seasonNumber: number | null
  episodeNumber: number | null
  seasonLabel: string | null
  partNumber: number | null
  partTotal: number | null
}

function numberingLabel(value: Numbered, t: (key: string, options?: Record<string, unknown>) => string) {
  if (value.numberingScheme === 'YEAR' && value.seasonLabel && value.episodeNumber != null) return t('series.numbering.yearEpisode', { label: value.seasonLabel, episode: value.episodeNumber })
  if (value.numberingScheme === 'PART_OF_TOTAL' && value.partNumber != null && value.partTotal != null) return t('series.numbering.part', { part: value.partNumber, total: value.partTotal })
  if (value.seasonNumber != null && value.episodeNumber != null) return t('series.numbering.seasonEpisode', { season: value.seasonNumber, episode: value.episodeNumber })
  if (value.episodeNumber != null) return t('series.numbering.episode', { episode: value.episodeNumber })
  return t('series.numbering.unknown')
}

function shortNumberingLabel(value: Numbered) {
  if (value.numberingScheme === 'YEAR' && value.seasonLabel && value.episodeNumber != null) return `${value.seasonLabel.replace('Jahrgang ', '')}/${value.episodeNumber}`
  if (value.numberingScheme === 'PART_OF_TOTAL' && value.partNumber != null && value.partTotal != null) return `${value.partNumber}/${value.partTotal}`
  if (value.seasonNumber != null && value.episodeNumber != null) return `S${String(value.seasonNumber).padStart(2, '0')} E${String(value.episodeNumber).padStart(2, '0')}`
  if (value.episodeNumber != null) return `E${value.episodeNumber}`
  return '\u2014'
}
